import { HttpRequest } from '@angular/common/http';

/**
 * Endpoints whose response is the same for every visitor, and so may be embedded in the
 * server-rendered HTML.
 *
 * This is an allow-list rather than a deny-list on purpose. `cookieSsrInterceptor` forwards
 * the visitor's own cookies, so every request made during a server render is authenticated
 * as that person — a deny-list would mean a new personalised endpoint is serialised into
 * the document until somebody notices. Here a new endpoint is simply not cached until it is
 * added, which costs a request and leaks nothing.
 *
 * Prefixes, matched against the path. Anything under a listed prefix that is not a plain
 * read (`/products/by-ids`, the vote endpoints) is a POST, and POSTs are excluded already.
 *
 * Intentionally absent (personalised — must never appear in the serialised HTML):
 *   /api/v1/auth        — current session / logged-in customer
 *   /api/v1/cart        — not fetched during SSR anyway (browser-only guard in CartService)
 *   /api/v1/customer/*  — account details
 *   /api/v1/order/*     — order history / payment details
 *   /api/v1/wishlist/*  — per-user wishlist
 */
const PUBLIC_API_PATH_PREFIXES: readonly string[] = [
  '/api/v1/additional-services',
  '/api/v1/aggregators/',
  '/api/v1/announcement',
  '/api/v1/attributes/ids',
  '/api/v1/banner',
  '/api/v1/blog/',
  '/api/v1/categories/',
  '/api/v1/info-pages/',
  '/api/v1/instagram/',
  '/api/v1/maintenance',
  '/api/v1/pages',
  '/api/v1/payment-methods',
  '/api/v1/product-reviews',
  '/api/v1/products',
  '/api/v1/shipping-methods',
  '/api/v1/store-reviews',
];

/**
 * Whether a request may be answered from the SSR transfer cache.
 *
 * Angular refuses on its own two grounds that this app trips on every single request, which
 * is why the cache was empty on every page: a request carrying a `cookie` header counts as
 * authenticated, and a response carrying `Set-Cookie` counts as non-cacheable. SSR forwards
 * the visitor's cookies, and the API answers with `Set-Cookie: clientId=...` when there are
 * none — so a first-time visitor failed the second rule and a returning one failed the
 * first. Both rules are switched off in `app.config.ts` and replaced by this list.
 */
export const isTransferCacheable = (req: HttpRequest<unknown>): boolean => {
  const path = req.url.startsWith('http') ? new URL(req.url).pathname : req.url;

  return PUBLIC_API_PATH_PREFIXES.some(prefix => path.startsWith(prefix));
};
